import React from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { useGameStore } from '../../store/gameStore';
import { X, Newspaper, Loader2 } from 'lucide-react';

export const NewsModal = ({ open, onClose, article, loading = false }) => {
  const selectedTheme = useGameStore((state) => state.selectedTheme);

  // Theme-specific styling
  const themeConfig = {
    noir: {
      accent: '#DC143C',
      paperBg: 'bg-amber-50',
      textColor: 'text-gray-900',
      newspaperName: 'THE DAILY CHRONICLE',
      fontStyle: 'font-serif',
      loadingTitle: 'EXTRA! EXTRA!',
      loadingMessage: 'Hot off the press...'
    },
    '90s_nyc': {
      accent: '#FF6B35',
      paperBg: 'bg-gray-100',
      textColor: 'text-gray-900',
      newspaperName: 'NEW YORK POST',
      fontStyle: 'font-sans',
      loadingTitle: 'BREAKING NEWS',
      loadingMessage: 'Story developing...'
    },
    miami_vice: {
      accent: '#FF1493',
      paperBg: 'bg-white',
      textColor: 'text-gray-900',
      newspaperName: 'MIAMI HERALD',
      fontStyle: 'font-sans',
      loadingTitle: 'THIS JUST IN',
      loadingMessage: 'Live from Miami...'
    }
  };

  const config = themeConfig[selectedTheme] || themeConfig.noir;

  // Show loading state
  if (loading && !article) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md p-0 bg-slate-900 border-2 overflow-hidden" style={{ borderColor: config.accent }}>
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1 lg:p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
          </button>

          <div className="p-8 flex flex-col items-center justify-center min-h-[300px]">
            {/* Newspaper masthead */}
            <div className="text-center mb-6">
              <h2 className="text-xs lg:text-sm tracking-[0.3em] text-white/60 mb-2">
                {config.newspaperName}
              </h2>
              <div className="w-24 h-0.5 bg-white/30 mx-auto" />
            </div>

            {/* Animated newspaper icon */}
            <div className="relative mb-6">
              <Newspaper
                className="w-16 h-16 animate-pulse"
                style={{ color: config.accent }}
              />
            </div>

            {/* Loading title */}
            <h1
              className={`text-2xl lg:text-3xl font-black text-white ${config.fontStyle} mb-3 tracking-tight animate-pulse`}
              style={{ color: config.accent }}
            >
              {config.loadingTitle}
            </h1>

            {/* Loading message */}
            <p className="text-sm lg:text-base text-gray-400 mb-6 italic">
              {config.loadingMessage}
            </p>

            {/* Spinner */}
            <Loader2
              className="w-8 h-8 animate-spin"
              style={{ color: config.accent }}
            />

            <p className="text-xs text-gray-500 mt-4">
              Printing your edition...
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Don't render if no article and not loading
  if (!article) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl lg:max-w-4xl max-h-[90vh] p-0 bg-slate-900 border-2 overflow-hidden flex flex-col" style={{ borderColor: config.accent }}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1 lg:p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
        >
          <X className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </button>

        {/* Scrollable content area */}
        <div className="overflow-y-auto flex-1">
          {/* Newspaper Image. The max-h clamp binds before aspect-[2/3] at every
              supported viewport, so the hero band is height-driven, not ratio-driven. */}
          {article.image ? (
            <div className="relative w-full aspect-[2/3] max-h-[35vh] lg:max-h-[45vh]">
              <img
                src={article.image}
                alt="Newspaper"
                className="w-full h-full object-cover object-top"
              />
            </div>
          ) : (
            <div className="w-full aspect-[2/3] max-h-[35vh] lg:max-h-[45vh] bg-amber-100 flex items-center justify-center">
              <Newspaper className="w-20 h-20 lg:w-28 lg:h-28 text-amber-300" />
            </div>
          )}

        {/* Article Text - The readable version */}
        <div className="p-6 lg:p-8 bg-slate-800">
          {/* Newspaper masthead */}
          <div className="text-center border-b-2 border-white/20 pb-2 lg:pb-3 mb-4 lg:mb-6">
            <h2 className="text-xs lg:text-sm tracking-[0.3em] text-white/60">
              {config.newspaperName}
            </h2>
          </div>

          {/* Headline - Large and bold */}
          <h1 className={`text-2xl md:text-3xl lg:text-4xl font-black text-white ${config.fontStyle} leading-tight mb-2 lg:mb-3 tracking-tight`}>
            {article.headline}
          </h1>

          {/* Subheadline */}
          <h2 className="text-sm md:text-base lg:text-lg font-medium text-gray-300 mb-4 lg:mb-6 italic">
            {article.subheadline}
          </h2>

          {/* Divider */}
          <div className="w-16 lg:w-24 h-0.5 bg-white/30 mb-4 lg:mb-6" />

          {/* Article body - Smaller text */}
          <div className={`text-sm lg:text-base text-gray-200 ${config.fontStyle} leading-relaxed lg:leading-loose space-y-3 lg:space-y-4`}>
            {article.article.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {/* Turn indicator */}
          {article.turn && (
            <div className="mt-4 lg:mt-6 pt-3 lg:pt-4 border-t border-white/20 text-xs lg:text-sm text-gray-400 text-right">
              Day {article.turn} of Investigation
            </div>
          )}
        </div>
        </div>

        {/* Dismiss button - Fixed at bottom */}
        <div className="p-4 lg:p-6 bg-slate-800 flex-shrink-0">
          <Button
            onClick={onClose}
            className="w-full font-semibold lg:text-lg lg:py-3"
            style={{ backgroundColor: config.accent }}
          >
            Continue Investigation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
